import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  PlusIcon, 
  EyeIcon,
  PencilIcon,
  ExclamationTriangleIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import { dbHelpers, supabase } from '../lib/supabase';
import toast from 'react-hot-toast';
import LeadModal from '../components/LeadModal';
import LeadActionModal from '../components/LeadActionModal';
import LeadDetailsModal from '../components/LeadDetailsModal';
import AdvancedSearch from '../components/AdvancedSearch';


const Leads = ({ user }) => {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [dateRangeFilter, setDateRangeFilter] = useState('all');
  const [specificDate, setSpecificDate] = useState('');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalLeads, setTotalLeads] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const leadsPerPage = 20;
  const [showModal, setShowModal] = useState(false);
  const [showActionModal, setShowActionModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [editingLead, setEditingLead] = useState(null);
  const [selectedLead, setSelectedLead] = useState(null);
  const [realtimeStatus, setRealtimeStatus] = useState('connecting');
  const [leadFormData, setLeadFormData] = useState({
    name: '',
    phone: '',
    source: ''
  });
  const [actionFormData, setActionFormData] = useState({
    action: '',
    nextAction: '',
    followUpDate: ''
  });

  // Function to check if a phone number is similar to existing leads
  const checkForSimilarPhone = useCallback((phone, currentLeadId = null) => {
    if (!phone || !leads.length) return false;
    
    // Normalize phone number (remove spaces, dashes, etc.)
    const normalizePhone = (num) => {
      return num.replace(/[\s\-()]/g, '').replace(/^\+91/, '').replace(/^91/, '');
    };
    
    const normalizedPhone = normalizePhone(phone);
    
    return leads.some(lead => {
      if (lead.id === currentLeadId) return false; // Don't compare with self
      if (!lead.phone) return false;
      
      const leadNormalizedPhone = normalizePhone(lead.phone);
      
      // Check for exact match
      if (normalizedPhone === leadNormalizedPhone) return true;
      
      // Check for similar numbers (last 8 digits match)
      if (normalizedPhone.length >= 8 && leadNormalizedPhone.length >= 8) {
        const phoneLast8 = normalizedPhone.slice(-8);
        const leadLast8 = leadNormalizedPhone.slice(-8);
        if (phoneLast8 === leadLast8) return true;
      }
      
      // Check for similar numbers (last 10 digits match)
      if (normalizedPhone.length >= 10 && leadNormalizedPhone.length >= 10) {
        const phoneLast10 = normalizedPhone.slice(-10);
        const leadLast10 = leadNormalizedPhone.slice(-10);
        if (phoneLast10 === leadLast10) return true;
      }
      
      return false;
    });
  }, [leads]);

  // Memoized duplicate detection for all leads
  const leadDuplicates = useMemo(() => {
    const duplicates = {};
    leads.forEach(lead => {
      if (lead.phone) {
        const isDuplicate = checkForSimilarPhone(lead.phone, lead.id);
        duplicates[lead.id] = isDuplicate;
        if (isDuplicate) {
          console.log(`Duplicate detected for lead ${lead.id} (${lead.name}): ${lead.phone}`);
        }
      }
    });
    console.log('Lead duplicates:', duplicates);
    return duplicates;
  }, [leads, checkForSimilarPhone]);

  const loadLeads = useCallback(async (page = 1, search = '', status = 'all', priority = 'all', dateRange = 'all', source = 'all', specificDateFilter = '') => {
    try {
      setLoading(true);
      console.log('Loading leads for user:', user.id, 'Page:', page, 'Filters:', { search, status, priority, dateRange, source });
      
      // Calculate offset for pagination
      const from = (page - 1) * leadsPerPage;
      const to = from + leadsPerPage - 1;
      
      // Build query
      let query = supabase
        .from('leads')
        .select('*', { count: 'exact' })
        .order('created_at', { ascending: false })
        .range(from, to);

      // Apply search filter
      if (search.trim()) {
        query = query.or(`name.ilike.%${search}%,email.ilike.%${search}%,phone.ilike.%${search}%,company.ilike.%${search}%,source.ilike.%${search}%`);
      }

      // Apply status filter
      if (status !== 'all') {
        query = query.eq('status', status);
      }

      // Apply priority filter
      if (priority !== 'all') {
        query = query.eq('priority', priority);
      }

      // Apply source filter
      if (source !== 'all') {
        query = query.eq('source', source);
      }

      // Apply date range filter
      if (dateRange !== 'all') {
        const now = new Date();
        let startDate, endDate;
        
        switch (dateRange) {
          case 'today':
            startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
            endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
            query = query.gte('created_at', startDate.toISOString()).lt('created_at', endDate.toISOString());
            break;
          case 'yesterday':
            startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
            endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
            query = query.gte('created_at', startDate.toISOString()).lt('created_at', endDate.toISOString());
            break;
          case 'this_week':
            // Get start of current week (Monday)
            const startOfWeek = new Date(now);
            startOfWeek.setDate(now.getDate() - now.getDay() + 1);
            startOfWeek.setHours(0, 0, 0, 0);
            query = query.gte('created_at', startOfWeek.toISOString());
            break;
          case 'last_week':
            // Get start of last week (Monday)
            const lastWeekStart = new Date(now);
            lastWeekStart.setDate(now.getDate() - now.getDay() - 6);
            lastWeekStart.setHours(0, 0, 0, 0);
            // Get end of last week (Sunday)
            const lastWeekEnd = new Date(now);
            lastWeekEnd.setDate(now.getDate() - now.getDay());
            lastWeekEnd.setHours(0, 0, 0, 0);
            query = query.gte('created_at', lastWeekStart.toISOString()).lt('created_at', lastWeekEnd.toISOString());
            break;
          case 'this_month':
            startDate = new Date(now.getFullYear(), now.getMonth(), 1);
            startDate.setHours(0, 0, 0, 0);
            query = query.gte('created_at', startDate.toISOString());
            break;
          case 'last_month':
            startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
            startDate.setHours(0, 0, 0, 0);
            endDate = new Date(now.getFullYear(), now.getMonth(), 1);
            endDate.setHours(0, 0, 0, 0);
            query = query.gte('created_at', startDate.toISOString()).lt('created_at', endDate.toISOString());
            break;
          case 'last_30_days':
            startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
            startDate.setHours(0, 0, 0, 0);
            query = query.gte('created_at', startDate.toISOString());
            break;
          case 'last_7_days':
            startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
            startDate.setHours(0, 0, 0, 0);
            query = query.gte('created_at', startDate.toISOString());
            break;
          case 'last_90_days':
            startDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
            startDate.setHours(0, 0, 0, 0);
            query = query.gte('created_at', startDate.toISOString());
            break;
          case 'this_quarter':
            const currentQuarter = Math.floor(now.getMonth() / 3);
            startDate = new Date(now.getFullYear(), currentQuarter * 3, 1);
            startDate.setHours(0, 0, 0, 0);
            query = query.gte('created_at', startDate.toISOString());
            break;
          case 'last_quarter':
            const lastQuarter = Math.floor(now.getMonth() / 3) - 1;
            if (lastQuarter < 0) {
              startDate = new Date(now.getFullYear() - 1, 9, 1); // October of last year
              endDate = new Date(now.getFullYear() - 1, 11, 31); // December of last year
            } else {
              startDate = new Date(now.getFullYear(), lastQuarter * 3, 1);
              endDate = new Date(now.getFullYear(), (lastQuarter + 1) * 3, 1);
            }
            startDate.setHours(0, 0, 0, 0);
            endDate.setHours(0, 0, 0, 0);
            query = query.gte('created_at', startDate.toISOString()).lt('created_at', endDate.toISOString());
            break;
          case 'this_year':
            startDate = new Date(now.getFullYear(), 0, 1);
            startDate.setHours(0, 0, 0, 0);
            query = query.gte('created_at', startDate.toISOString());
            break;
          case 'last_year':
            startDate = new Date(now.getFullYear() - 1, 0, 1);
            startDate.setHours(0, 0, 0, 0);
            endDate = new Date(now.getFullYear(), 0, 1);
            endDate.setHours(0, 0, 0, 0);
            query = query.gte('created_at', startDate.toISOString()).lt('created_at', endDate.toISOString());
            break;
          case 'specific_date':
            if (specificDateFilter) {
              const selectedDate = new Date(specificDateFilter);
              selectedDate.setHours(0, 0, 0, 0);
              const selectedDateEnd = new Date(selectedDate);
              selectedDateEnd.setHours(23, 59, 59, 999);
              query = query.gte('created_at', selectedDate.toISOString()).lte('created_at', selectedDateEnd.toISOString());
            }
            break;
          default:
            break;
        }
      }

      const { data, error, count } = await query;

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }
      
      console.log('Leads loaded:', data?.length || 0, 'Total:', count);
      setLeads(data || []);
      setTotalLeads(count || 0);
      setTotalPages(Math.ceil((count || 0) / leadsPerPage));
    } catch (error) {
      console.error('Leads error:', error);
      toast.error(`Error loading leads: ${error.message}`);
      setLeads([]);
      setTotalLeads(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  }, [user.id, leadsPerPage]);

  const setupRealtimeSubscription = useCallback(() => {
    // Clean up any existing subscription first
    if (window.leadsSubscription) {
      supabase.removeChannel(window.leadsSubscription);
    }
    
    // Create a unique channel name
    const channelName = `leads_changes_${user.id}_${Date.now()}`;
    
    const subscription = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'leads'
        },
        (payload) => {
          // Check if this lead belongs to the current user
          const isRelevantLead = payload.new?.assigned_to === user.id || 
                                payload.old?.assigned_to === user.id;
          
          if (payload.eventType === 'INSERT' && isRelevantLead) {
            setLeads(prevLeads => {
              // Check if lead is already in the list
              const exists = prevLeads.find(lead => lead.id === payload.new.id);
              if (!exists) {
                toast.success(`New lead added: ${payload.new.name || 'Unknown'}`);
                return [payload.new, ...prevLeads];
              }
              return prevLeads;
            });
          } else if (payload.eventType === 'UPDATE' && isRelevantLead) {
            setLeads(prevLeads => 
              prevLeads.map(lead => 
                lead.id === payload.new.id ? payload.new : lead
              )
            );
            toast.success(`Lead updated: ${payload.new.name || 'Unknown'}`);
          } else if (payload.eventType === 'DELETE' && isRelevantLead) {
            setLeads(prevLeads => 
              prevLeads.filter(lead => lead.id !== payload.old.id)
            );
            toast.success('Lead deleted');
          }
        }
      )
      .subscribe((status) => {
        setRealtimeStatus(status);
        // Only show toast on first connection, not on reconnections
        if (status === 'SUBSCRIBED' && !window.realtimeToastShown) {
          toast.success('Real-time updates enabled');
          window.realtimeToastShown = true;
        }
      });

    // Store subscription for cleanup
    window.leadsSubscription = subscription;
  }, [user.id]);

  const cleanupRealtimeSubscription = () => {
    if (window.leadsSubscription) {
      supabase.removeChannel(window.leadsSubscription);
      window.leadsSubscription = null;
    }
  };

  useEffect(() => {
    if (user) {
      loadLeads(currentPage, searchTerm, statusFilter, priorityFilter, dateRangeFilter, sourceFilter, specificDate);
      setupRealtimeSubscription();
    }

    // Cleanup subscription on unmount
    return () => {
      if (user) {
        cleanupRealtimeSubscription();
      }
    };
  }, [user, loadLeads, setupRealtimeSubscription, currentPage, searchTerm, statusFilter, priorityFilter, dateRangeFilter, sourceFilter, specificDate]);

  const handleAddLead = () => {
    setEditingLead(null);
    // Initialize form data for new lead
    setLeadFormData({
      name: '',
      phone: '',
      source: ''
    });
    setShowModal(true);
  };



  const handleLeadAction = async (action, nextAction, followUpDate = null) => {
    try {
      const leadId = selectedLead.id;
      console.log('Handling lead action:', { leadId, action, nextAction, followUpDate });
      
      const updates = {
        last_action: action,
        last_action_date: new Date().toISOString()
      };

      // Handle follow-up date based on next action
      if (nextAction === 'Follow Up') {
        if (followUpDate) {
          // Ensure the date is in the correct format
          const followUpDateObj = new Date(followUpDate);
          updates.follow_up_date = followUpDateObj.toISOString();
          updates.status = 'follow_up';
          console.log('Setting follow-up date:', followUpDateObj.toISOString());
        } else {
          toast.error('Follow-up date is required when selecting Follow Up');
          return;
        }
      } else if (nextAction === 'Converted') {
        updates.status = 'converted';
        updates.follow_up_date = null;
        
        // Create order with auto-populated address from lead
        console.log('Creating order for lead:', leadId);
        
        // First get the lead details to populate address
        const { data: leadData, error: leadError } = await supabase
          .from('leads')
          .select('address, city, state, pincode')
          .eq('id', leadId)
          .single();
          
        if (leadError) {
          console.error('Error fetching lead details:', leadError);
        }
        
        const { data: orderData, error: orderError } = await supabase
          .from('orders')
          .insert([{
            lead_id: leadId,
            user_id: user.id,
            conversion_date: new Date().toISOString(),
            status: 'pending_shipping',
            address_line_1: leadData?.address || '',
            city: leadData?.city || '',
            state: leadData?.state || '',
            pin: leadData?.pincode || ''
          }])
          .select();
          
        if (orderError) {
          console.error('Error creating order:', orderError);
          toast.error(`Error creating order: ${orderError.message}`);
          return;
        }
        
        console.log('Order created successfully:', orderData);
        toast.success('Lead converted and order created successfully');
      } else if (nextAction === 'Close') {
        updates.status = 'closed';
        updates.follow_up_date = null;
      }

      console.log('Updating lead with:', updates);
      const { error: updateError } = await dbHelpers.updateLead(leadId, updates);
      
      if (updateError) {
        console.error('Error updating lead:', updateError);
        toast.error(`Error updating lead: ${updateError.message}`);
        return;
      }

      console.log('Lead updated successfully');
      toast.success('Action updated successfully');
      setShowActionModal(false);
      setSelectedLead(null);
      loadLeads();
    } catch (error) {
      console.error('Error updating action:', error);
      toast.error(`Error updating action: ${error.message}`);
    }
  };


  const openDetailsModal = (lead) => {
    navigate(`/leads/${lead.id}`);
  };

  const handleDeleteLead = async (leadId) => {
    if (!window.confirm('Are you sure you want to delete this lead? This action cannot be undone.')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('leads')
        .delete()
        .eq('id', leadId);

      if (error) throw error;

      toast.success('Lead deleted successfully');
      loadLeads(currentPage, searchTerm, statusFilter, priorityFilter, dateRangeFilter, sourceFilter, specificDate);
    } catch (error) {
      console.error('Error deleting lead:', error);
      toast.error(`Error deleting lead: ${error.message}`);
    }
  };

  const handleEditLead = (lead) => {
    setEditingLead(lead);
    // Initialize form data for editing
    setLeadFormData({
      name: lead.name || '',
      phone: lead.phone || '',
      source: lead.source || ''
    });
    setShowModal(true);
  };

  const handleSearch = (value) => {
    setSearchTerm(value);
    setCurrentPage(1); // Reset to first page when searching
  };

  const handleFilter = (filters) => { // New function for advanced filters
    setStatusFilter(filters.status);
    setPriorityFilter(filters.priority);
    setDateRangeFilter(filters.dateRange);
    setSourceFilter(filters.source);
    setSpecificDate(filters.specificDate || '');
    setCurrentPage(1); // Reset to first page when filtering
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleSaveLead = async (leadData) => {
    try {
      if (editingLead) {
        await dbHelpers.updateLead(editingLead.id, leadData);
        toast.success('Lead updated successfully');
      } else {
        // Check for similar phone numbers before creating
        if (checkForSimilarPhone(leadData.phone)) {
          toast.error('⚠️ Warning: A lead with a similar phone number already exists!', {
            duration: 5000,
            style: {
              background: '#fef3c7',
              color: '#92400e',
              border: '1px solid #f59e0b'
            }
          });
        }
        
        await dbHelpers.createLead({ ...leadData, user_id: user.id, assigned_to: user.id });
        toast.success('Lead created successfully');
      }
      setShowModal(false);
      setEditingLead(null);
      loadLeads(currentPage, searchTerm, statusFilter, priorityFilter, dateRangeFilter, sourceFilter, specificDate);
    } catch (error) {
      console.error('Error saving lead:', error);
      toast.error(`Error saving lead: ${error.message}`);
    }
  };

  // No need for client-side filtering since we're doing it in the database
  const filteredLeads = leads;

  // Debug logging for filtering
  console.log('=== LEADS DISPLAY DEBUG ===');
  console.log('Total leads:', leads.length);
  console.log('Search term:', searchTerm);
  console.log('Status filter:', statusFilter);
  console.log('Filtered leads:', filteredLeads.length);
  console.log('Sample lead:', leads[0]);
  console.log('=== END LEADS DISPLAY DEBUG ===');


  const getPriorityColor = (priority) => {
    const priorityLower = priority?.toLowerCase();
    switch (priorityLower) {
      case 'high': return 'bg-red-100 text-red-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Leads</h1>
          <p className="text-gray-600">Manage your sales leads and opportunities</p>
          <div className="flex items-center mt-1">
            <div className={`flex items-center text-sm ${
              realtimeStatus === 'SUBSCRIBED' 
                ? 'text-green-600' 
                : realtimeStatus === 'connecting' 
                ? 'text-yellow-600' 
                : 'text-red-600'
            }`}>
              <div className={`w-2 h-2 rounded-full mr-2 ${
                realtimeStatus === 'SUBSCRIBED' 
                  ? 'bg-green-500 animate-pulse' 
                  : realtimeStatus === 'connecting' 
                  ? 'bg-yellow-500 animate-spin' 
                  : 'bg-red-500'
              }`}></div>
              {realtimeStatus === 'SUBSCRIBED' 
                ? 'Real-time updates active' 
                : realtimeStatus === 'connecting' 
                ? 'Connecting...' 
                : 'Connection lost'}
            </div>
          </div>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={handleAddLead}
            className="btn-primary flex items-center"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Add Lead
          </button>
        </div>
      </div>

      {/* Advanced Search */}
      <div className="card p-4">
        <AdvancedSearch
          onSearch={handleSearch}
          onFilter={handleFilter}
          searchTerm={searchTerm}
          statusFilter={statusFilter}
          loading={loading}
        />
      </div>

      {/* Leads Table */}
      <div className="card overflow-hidden">
        {(() => {
          if (filteredLeads.length === 0) {
            return (
              <div className="px-6 py-8 text-center text-gray-500">
                {leads.length === 0 ? 'No leads found. Create your first lead to get started!' : 'No leads match your search criteria.'}
              </div>
            );
          }
          
          return (
            <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    S.No.
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Mobile No
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Priority
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Source
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
                                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredLeads.map((lead, index) => (
                      <tr key={lead.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {(currentPage - 1) * leadsPerPage + index + 1}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {lead.created_at ? new Date(lead.created_at).toLocaleDateString() : 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center space-x-2">
                            <div className="text-sm font-medium text-gray-900">
                              {lead.name || 'N/A'}
                            </div>
                            {leadDuplicates[lead.id] && (
                              <div className="flex items-center" title="Similar phone number found in existing leads">
                                <ExclamationTriangleIcon className="h-4 w-4 text-amber-500" />
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className={`flex items-center space-x-2 ${leadDuplicates[lead.id] ? 'bg-amber-50 border-l-4 border-amber-400 pl-2' : ''}`}>
                            <div className="text-sm text-gray-900">
                              {lead.phone || 'N/A'}
                            </div>
                            {leadDuplicates[lead.id] && (
                              <div className="flex items-center" title="Similar phone number found in existing leads">
                                <ExclamationTriangleIcon className="h-4 w-4 text-amber-500" />
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(lead.priority)}`}>
                            {lead.priority || 'N/A'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {lead.source || 'N/A'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex justify-end space-x-2">
                            <button
                              onClick={() => openDetailsModal(lead)}
                              className="text-blue-600 hover:text-blue-900"
                              title="View Details"
                            >
                              <EyeIcon className="h-5 w-5" />
                            </button>
                            <button
                              onClick={() => handleEditLead(lead)}
                              className="text-green-600 hover:text-green-900"
                              title="Edit Lead"
                            >
                              <PencilIcon className="h-5 w-5" />
                            </button>
                            <button
                              onClick={() => handleDeleteLead(lead.id)}
                              className="text-red-600 hover:text-red-900"
                              title="Delete Lead"
                            >
                              <TrashIcon className="h-5 w-5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
              </table>
            </div>
          );
        })()}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="card p-4">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Showing {((currentPage - 1) * leadsPerPage) + 1} to {Math.min(currentPage * leadsPerPage, totalLeads)} of {totalLeads} leads
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              
              {/* Page numbers */}
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                
                return (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    className={`px-3 py-1 text-sm border rounded-md ${
                      currentPage === pageNum
                        ? 'bg-primary-600 text-white border-primary-600'
                        : 'border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
              
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lead Modal */}
      {showModal && (
        <LeadModal
          lead={editingLead}
          onSave={handleSaveLead}
          onClose={() => setShowModal(false)}
          formData={leadFormData}
          setFormData={setLeadFormData}
        />
      )}

      {/* Lead Action Modal */}
      {showActionModal && selectedLead && (
        <LeadActionModal
          lead={selectedLead}
          onSave={handleLeadAction}
          onClose={() => {
            setShowActionModal(false);
            setSelectedLead(null);
          }}
          formData={actionFormData}
          setFormData={setActionFormData}
        />
      )}

      {/* Lead Details Modal */}
      {showDetailsModal && selectedLead && (
        <LeadDetailsModal
          lead={selectedLead}
          onClose={() => {
            setShowDetailsModal(false);
            setSelectedLead(null);
          }}
          onUpdate={loadLeads}
        />
      )}


    </div>
  );
};

export default Leads;
